import React, { useEffect, useRef } from 'react';
import WaveSurfer from 'wavesurfer.js';
import Regions from 'wavesurfer.js/dist/plugins/regions.esm.js';

interface AudioWaveformProps {
  audioUrl: string;
  currentTime: number;
  duration: number;
  onSeek: (time: number) => void;
  clipStartSec?: number;
  clipEndSec?: number;
  height?: number;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const AudioWaveform: React.FC<AudioWaveformProps> = ({
  audioUrl,
  currentTime,
  duration,
  onSeek,
  clipStartSec,
  clipEndSec,
  height = 60,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const waveSurferRef = useRef<WaveSurfer | null>(null);
  const readyRef = useRef(false);
  const onSeekRef = useRef(onSeek);

  useEffect(() => {
    onSeekRef.current = onSeek;
  }, [onSeek]);

  useEffect(() => {
    if (!audioUrl || !containerRef.current) return;

    readyRef.current = false;
    const hasClip = Number.isFinite(clipStartSec) && Number.isFinite(clipEndSec) && (clipEndSec ?? 0) > (clipStartSec ?? 0);
    const regionStart = hasClip ? Math.max(0, clipStartSec ?? 0) : 0;
    const regionEnd = hasClip ? Math.max(regionStart, clipEndSec ?? regionStart) : undefined;
    const regions = Regions.create();

    const waveSurfer = WaveSurfer.create({
      container: containerRef.current,
      url: audioUrl,
      height,
      waveColor: '#d9d9d9',
      progressColor: '#1890ff',
      cursorColor: '#1677ff',
      cursorWidth: 2,
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
      normalize: true,
      dragToSeek: true,
      hideScrollbar: true,
      plugins: [regions],
    });

    waveSurferRef.current = waveSurfer;

    const handleReady = () => {
      readyRef.current = true;
      if (regionEnd != null) {
        regions.addRegion({
          start: regionStart,
          end: regionEnd,
          color: 'rgba(24, 144, 255, 0.18)',
          drag: false,
          resize: false,
        });
      }
      const effectiveMax = regionEnd ?? (duration > 0 ? duration : waveSurfer.getDuration());
      waveSurfer.setTime(clamp(currentTime, regionStart, Math.max(regionStart, effectiveMax)));
    };

    const handleInteraction = (newTime: number) => {
      const effectiveMax = regionEnd ?? (duration > 0 ? duration : waveSurfer.getDuration());
      onSeekRef.current(clamp(newTime, regionStart, Math.max(regionStart, effectiveMax)));
    };

    waveSurfer.on('ready', handleReady);
    waveSurfer.on('interaction', handleInteraction);

    return () => {
      readyRef.current = false;
      waveSurfer.destroy();
      waveSurferRef.current = null;
    };
  }, [audioUrl, clipEndSec, clipStartSec, duration, height]);

  useEffect(() => {
    const waveSurfer = waveSurferRef.current;
    if (!waveSurfer || !readyRef.current) {
      return;
    }

    const hasClip = Number.isFinite(clipStartSec) && Number.isFinite(clipEndSec) && (clipEndSec ?? 0) > (clipStartSec ?? 0);
    const minTime = hasClip ? Math.max(0, clipStartSec ?? 0) : 0;
    const maxTime = hasClip
      ? Math.max(minTime, clipEndSec ?? minTime)
      : Math.max(minTime, duration > 0 ? duration : waveSurfer.getDuration());
    const targetTime = clamp(currentTime, minTime, maxTime);

    if (Math.abs(waveSurfer.getCurrentTime() - targetTime) > 0.05) {
      waveSurfer.setTime(targetTime);
    }
  }, [clipEndSec, clipStartSec, currentTime, duration]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        minHeight: `${height}px`,
        cursor: audioUrl ? 'pointer' : 'default',
      }}
    />
  );
};

export default AudioWaveform;
